export const sleep = (time = 1000): Promise<void> => {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve();
    }, time);
  });
};

export const shortenAddress = (
  address: string,
  startLength: number = 6,
  endLength: number = 6
): string => {
  if (!address) return '';

  const start = address.slice(0, startLength);
  const end = address.slice(-endLength);

  return `${start}…${end}`;
};



const getLatestDate = (times: any) => {
  return new Date(
    Math.max(...times.map((date: string | Date) => new Date(date)))
  );
};

export const isLatestDate = (times: (string | null )[] | undefined , selectedDate: string) => {
  const latestDate = getLatestDate(times);
  return new Date(selectedDate).getTime() === latestDate.getTime();
};

/**
 * Converts a string to hex format with 0x prefix
 * @param str - The string to convert
 * @returns Hex string with 0x prefix
 */
export const stringToHex = (str: string): string => {
  // Encode string into UTF-8 bytes
  const encoder = new TextEncoder();
  const bytes = encoder.encode(str);

  // Convert each byte to hex and join
  const hex = Array.from(bytes)
    .map(b => b.toString(16).padStart(2, "0"))
    .join("");

  return "0x" + hex;
};

/**
 * Converts a hex string back to original string
 * @param hex - The hex string to convert (with or without 0x prefix)
 * @returns Original string
 */
export const hexToString = (hex: string): string => {
  // Remove 0x if present
  hex = hex.startsWith("0x") ? hex.slice(2) : hex;

  // Convert hex back to bytes
  const bytes = new Uint8Array(
    hex.match(/.{1,2}/g)?.map(byte => parseInt(byte, 16)) || []
  );

  // Decode UTF-8 bytes back to string
  const decoder = new TextDecoder();
  return decoder.decode(bytes);
};

/**
 * Splits a long hash into multiple lines for better UI display
 * @param hash - The hash string to split
 * @param charsPerLine - Number of characters per line (default: 52)
 * @returns Array of hash segments with copy information
 */
export const splitHashIntoLines = (hash: string, charsPerLine: number = 30): Array<{
  text: string;
  fullText: string;
  isLast: boolean;
}> => {
  if (!hash) return [];
  
  // Remove 0x prefix if present for splitting
  const cleanHash = hash.startsWith('0x') ? hash.slice(2) : hash;
  
  const lines: Array<{
    text: string;
    fullText: string;
    isLast: boolean;
  }> = [];
  
  for (let i = 0; i < cleanHash.length; i += charsPerLine) {
    const lineText = cleanHash.slice(i, i + charsPerLine);
    const isLast = i + charsPerLine >= cleanHash.length;
    
    lines.push({
      text: lineText,
      fullText: hash, // Full original hash for copying
      isLast
    });
  }
  
  return lines;
};

/**
 * Ensures a hex string is valid BytesLike for Ethers.js
 * Validates that the hex string starts with "0x" and has an even number of hex characters
 * If the hex portion has odd length, prepends "0" to make it even
 * @param value - The hex string to validate
 * @returns A valid BytesLike value with even number of hex characters
 */
export const ensureValidBytesLike = (value: string | undefined): string => {
  // Return default value if input is falsy
  if (!value || value === '0x00' || value === '0x') {
    return '0x00';
  }

  // Must start with 0x
  if (!value.startsWith('0x')) {
    return '0x00';
  }

  // Get the hex portion after 0x
  const hexPortion = value.slice(2);
  
  // If hex portion is empty or has odd length, pad with leading 0
  if (hexPortion.length === 0) {
    return '0x00';
  }
  
  if (hexPortion.length % 2 !== 0) {
    // Odd length: add leading zero to make even
    return '0x0' + hexPortion;
  }

  // Even length: return as is
  return value;
};

/**
 * Derives the public key (wallet address) from a private key
 * @param privateKey - The private key in hex format
 * @returns The wallet address derived from the private key
 */
export const getAddressFromPrivateKey = (privateKey: string): string => {
  const { ethers } = require('ethers');
  const wallet = new ethers.Wallet(privateKey);
  return wallet.address;
};

